import axios from 'axios';
import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faEye,
  faEyeSlash,
  faShield,
} from '@fortawesome/free-solid-svg-icons';
import { platformAuthService } from '@/services/platform-auth.service';
import { usePlatformAuthStore } from '@/stores/platform-auth.store';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/shared/FormField';
import { toast } from '@/lib/swal';
import { PLATFORM_ROUTES } from '@/routes/platform-routes';

export function PlatformLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setAuth = usePlatformAuthStore((s) => s.setAuth);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const e: Record<string, string> = {};
    if (!email.trim() || !email.includes('@')) e.email = 'Enter a valid email address';
    if (!password) e.password = 'Enter your password';
    return e;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setLoading(true);
    try {
      const res = await platformAuthService.login(email.trim().toLowerCase(), password);
      // Pass the fresh token directly — the store is not yet populated at this point
      const user = await platformAuthService.getMe(res.accessToken);
      setAuth(user, res.accessToken, res.refreshToken);
      const redirect = searchParams.get('redirect');
      navigate(redirect ? decodeURIComponent(redirect) : PLATFORM_ROUTES.DASHBOARD, { replace: true });
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        toast.error('Invalid email or password.');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-[400px]">
        {/* Brand */}
        <div className="mb-8 flex flex-col items-center gap-4">
          <div
            className="flex h-14 w-14 items-center justify-center rounded-2xl ring-2 ring-primary/30"
            style={{ background: '#283618' }}
          >
            <FontAwesomeIcon icon={faShield} className="h-7 w-7 text-white" />
          </div>
          <div className="text-center">
            <h1 className="text-xl font-semibold text-foreground">Service Portal</h1>
            <p className="mt-0.5 text-xs font-semibold tracking-widest text-primary uppercase">
              Platform Console
            </p>
          </div>
        </div>

        {/* Card */}
        <div className="rounded-2xl border border-border bg-card p-8 shadow-sm">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-foreground">Sign in</h2>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Platform administrator access only.
            </p>
          </div>

          <form onSubmit={handleSubmit} noValidate className="space-y-5">
            <FormField label="Email Address" htmlFor="platform-email" required error={errors.email}>
              <Input
                id="platform-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@platform.com"
                autoComplete="email"
                autoFocus
                disabled={loading}
              />
            </FormField>

            <FormField label="Password" htmlFor="platform-password" required error={errors.password}>
              <div className="relative">
                <Input
                  id="platform-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  disabled={loading}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} className="h-4 w-4" />
                </button>
              </div>
            </FormField>

            <Button
              type="submit"
              className="mt-1 w-full min-h-[44px]"
              disabled={loading}
              aria-disabled={loading}
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign in to Platform'
              )}
            </Button>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          This is a restricted area. Unauthorized access is prohibited.
        </p>
      </div>
    </div>
  );
}
